import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  CurrencyInr, Coins, Gift, ChartPieSlice, Plus, Trash, PencilSimple,
  Check, X, FloppyDisk, ToggleLeft, ToggleRight, ArrowUp, ArrowDown,
  Sparkle, ListBullets, WhatsappLogo, Copy, ShareNetwork,
} from '@phosphor-icons/react';

const RELATION_OPTIONS = [
  'Father side', 'Mother side', 'Spouse side',
  'Friend', 'Colleague', 'Neighbour', 'Relative', 'Other',
];
const PAYMENT_MODES = ['Cash', 'UPI', 'Bank Transfer', 'Cheque'];

const formatINR = (n) =>
  '₹' + (Number(n || 0)).toLocaleString('en-IN', { maximumFractionDigits: 0 });

// ============================================================
// MoneyTab — Budget + Shagun + Summary
// ============================================================
export default function MoneyTab({ event }) {
  const { api } = useAuth();
  const [sub, setSub] = useState('summary');
  const [showShare, setShowShare] = useState(false);

  const handleExport = async (view) => {
    try {
      const url = `/events/${event.event_id}/money/export.xlsx${view === 'share' ? '?view=share' : ''}`;
      const res = await api.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${(event.title || 'event').replace(/[\\/]/g, '-')} - money${view === 'share' ? ' (shared view)' : ''}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      alert('Excel export failed. Please try again.');
      console.error(e);
    }
  };
  const [excelMenuOpen, setExcelMenuOpen] = useState(false);

  return (
    <div className="space-y-4" data-testid="money-section">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-1 p-1 rounded-2xl w-fit" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
          {[
            { id: 'summary', label: 'Summary', icon: ChartPieSlice },
            { id: 'budget', label: 'Budget', icon: CurrencyInr },
            { id: 'shagun', label: 'Shagun', icon: Gift },
          ].map(s => {
            const Icon = s.icon;
            return (
              <button key={s.id} onClick={() => setSub(s.id)} data-testid={`money-sub-${s.id}`}
                className={`px-4 py-2 rounded-xl text-xs font-semibold transition-all flex items-center gap-1.5 ${sub === s.id ? 'bg-[#FF6B35]/15 text-[#FF6B35]' : 'text-neutral-400 hover:text-white hover:bg-white/5'}`}>
                <Icon size={14} /> {s.label}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <button onClick={() => setExcelMenuOpen(s => !s)}
              data-testid="money-export-xlsx-btn"
              title="Download Excel — full view (admin) or shared view (filtered)"
              className="px-3 py-2 rounded-full bg-blue-500/15 text-blue-400 border border-blue-500/30 text-xs font-semibold flex items-center gap-1.5 hover:bg-blue-500/25">
              <FloppyDisk size={12} /> Excel ▾
            </button>
            {excelMenuOpen && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setExcelMenuOpen(false)} />
                <div className="absolute right-0 mt-1 z-40 min-w-[200px] rounded-xl glass-card p-1.5"
                  data-testid="excel-menu">
                  <button onClick={() => { handleExport('full'); setExcelMenuOpen(false); }}
                    data-testid="excel-full-btn"
                    className="w-full text-left px-3 py-2 rounded-lg hover:bg-white/5 text-xs">
                    <div className="font-semibold text-white">Full view</div>
                    <div className="text-[10px] text-neutral-500">Everything — admin / accountant</div>
                  </button>
                  <button onClick={() => { handleExport('share'); setExcelMenuOpen(false); }}
                    data-testid="excel-share-btn"
                    className="w-full text-left px-3 py-2 rounded-lg hover:bg-white/5 text-xs">
                    <div className="font-semibold text-white">Shared view</div>
                    <div className="text-[10px] text-neutral-500">Exactly what your share-link viewers see</div>
                  </button>
                </div>
              </>
            )}
          </div>
          <button onClick={() => setShowShare(true)}
            data-testid="money-share-link-btn"
            title="Generate a read-only public link to share with spouse / accountant"
            className="px-3 py-2 rounded-full bg-purple-500/15 text-purple-400 border border-purple-500/30 text-xs font-semibold flex items-center gap-1.5 hover:bg-purple-500/25">
            <ListBullets size={12} /> Share Sheet
          </button>
        </div>
      </div>

      {sub === 'summary' && <SummaryView event={event} />}
      {sub === 'budget' && <BudgetView event={event} />}
      {sub === 'shagun' && <ShagunView event={event} />}

      {showShare && (
        <ShareSheetModal event={event} onClose={() => setShowShare(false)} />
      )}
    </div>
  );
}

// ------------------------------------------------------------
// Summary
// ------------------------------------------------------------
function SummaryView({ event }) {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/events/${event.event_id}/money/summary`);
      setData(r.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api, event.event_id]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Spinner />;
  if (!data) return <p className="text-sm text-neutral-400">No data.</p>;

  const t = data.totals;
  const isProfit = t.net_cost < 0;
  const overBudget = t.budget_remaining < 0;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Budget" value={formatINR(t.planned)} color="#A855F7" />
        <KPI label="Advance Paid" value={formatINR(t.advance_paid ?? t.actual_spent)} color="#EF4444"
          sub={overBudget ? `${formatINR(Math.abs(t.budget_remaining))} over` : `${formatINR(Math.max(0,t.budget_remaining))} left`}
          subColor={overBudget ? '#EF4444' : '#22C55E'} />
        <KPI label="Shagun Received" value={formatINR(t.shagun_received)} color="#22C55E"
          sub={`${data.shagun_count} envelopes`} />
        <KPI label={isProfit ? 'Net Profit' : 'Net Cost'}
          value={formatINR(Math.abs(t.net_cost))}
          color={isProfit ? '#22C55E' : '#FF6B35'}
          sub={isProfit ? 'You received more than spent' : 'Spent − received'} />
      </div>

      {/* Settlement row — only shown once at least one line has its final cost set */}
      {t.any_final_set && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <KPI label="Final Cost (settled)" value={formatINR(t.final_total)} color="#FBBF24"
            sub="Sum of vendor final bills" />
          <KPI label="Balance Due"
            value={formatINR(Math.abs(t.balance_due))}
            color={t.balance_due > 0 ? '#EF4444' : '#22C55E'}
            sub={t.balance_due > 0 ? 'Still to pay vendors' : t.balance_due < 0 ? 'Vendor refund pending' : 'All cleared ✓'} />
          <KPI label="Variation"
            value={(t.variation === 0 ? '₹0' : (t.variation > 0 ? '+' : '-') + formatINR(Math.abs(t.variation)))}
            color={t.variation > 0 ? '#EF4444' : t.variation < 0 ? '#22C55E' : '#A3A3A3'}
            sub={t.variation > 0 ? 'Over budget' : t.variation < 0 ? 'Under budget — nice!' : 'On budget'} />
        </div>
      )}

      <div className="glass-card p-5">
        <h4 className="font-heading text-sm font-bold mb-3 flex items-center gap-2">
          <CurrencyInr size={14} className="text-[#FF6B35]" /> Spend by Category
        </h4>
        {data.category_breakdown.length === 0 ? (
          <p className="text-xs text-neutral-500 py-4 text-center">No budget lines or expenses yet.</p>
        ) : (
          <div className="space-y-2">
            {data.category_breakdown.map(c => {
              const pct = c.planned > 0 ? Math.min(100, Math.round(c.actual * 100 / c.planned)) : 0;
              const over = c.actual > c.planned && c.planned > 0;
              return (
                <div key={c.category} className="text-xs">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-neutral-300 font-medium">{c.category}</span>
                    <span className="text-neutral-400">
                      <span className={over ? 'text-red-400 font-bold' : 'text-white font-bold'}>{formatINR(c.actual)}</span>
                      {c.planned > 0 && <> / <span className="text-neutral-500">{formatINR(c.planned)}</span></>}
                    </span>
                  </div>
                  {c.planned > 0 && (
                    <div className="w-full h-1.5 rounded-full bg-white/5 overflow-hidden">
                      <div className="h-full rounded-full transition-all"
                        style={{ width: `${pct}%`, background: over ? '#EF4444' : pct > 80 ? '#F59E0B' : '#22C55E' }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {data.relation_breakdown.length > 0 && (
        <div className="glass-card p-5">
          <h4 className="font-heading text-sm font-bold mb-3 flex items-center gap-2">
            <Gift size={14} className="text-green-400" /> Shagun by Relation
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {data.relation_breakdown.map(r => (
              <div key={r.relation} className="px-3 py-2 rounded-xl bg-white/5 flex items-center justify-between">
                <span className="text-xs text-neutral-300">{r.relation}</span>
                <span className="text-sm font-bold text-green-400">{formatINR(r.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------
// Budget
// ------------------------------------------------------------
function BudgetView({ event }) {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showExpense, setShowExpense] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showCats, setShowCats] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/events/${event.event_id}/money/budget`);
      setData(r.data);
    } catch (e) { console.error(e); }
  }, [api, event.event_id]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (lineId) => {
    if (!window.confirm('Delete this budget line?')) return;
    await api.delete(`/events/${event.event_id}/money/budget/${lineId}`);
    load();
  };

  if (!data) return <Spinner />;
  const enabledCats = data.categories.filter(c => c.enabled).map(c => c.name);
  const t = data.totals;

  return (
    <div className="space-y-3">
      <div className="glass-card p-4 flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[180px]">
          <h3 className="font-heading text-sm font-bold">Budget Tracker</h3>
          <p className="text-xs text-neutral-400 mt-1">
            Plan vs Actual. Actuals come from Cashbook entries tagged to this event.
          </p>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-neutral-500">Budget</div>
          <div className="text-base font-bold text-white">{formatINR(t.planned)}</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-neutral-500">Advance</div>
          <div className="text-base font-bold text-green-400">{formatINR(t.advance_paid ?? t.actual)}</div>
        </div>
        {t.any_final_set && (
          <>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-neutral-500">Final*</div>
              <div className="text-base font-bold text-amber-400">{formatINR(t.final_total)}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-neutral-500">Balance Due</div>
              <div className={`text-base font-bold ${t.balance_due > 0 ? 'text-red-400' : 'text-green-400'}`}>{formatINR(Math.abs(t.balance_due))}</div>
            </div>
          </>
        )}
        <button onClick={() => setShowCats(s => !s)} className="px-3 py-2 rounded-full border border-white/10 text-xs font-medium hover:bg-white/5">
          {showCats ? 'Hide' : 'Edit'} Categories
        </button>
        <button onClick={() => setShowExpense(true)} data-testid="budget-add-expense-btn"
          className="px-4 py-2 rounded-full bg-green-500/15 text-green-400 border border-green-500/30 text-xs font-semibold flex items-center gap-1.5 hover:bg-green-500/25"
          title="Book a paid expense — creates a Cashbook entry auto-tagged to this event">
          <Plus size={14} weight="bold" /> Add Expense
        </button>
        <button onClick={() => setShowAdd(true)} data-testid="budget-add-btn"
          className="px-4 py-2 rounded-full bg-[#FF6B35] text-white text-xs font-semibold flex items-center gap-1.5 hover:opacity-90"
          title="Plan a new budget line (not yet spent)">
          <Plus size={14} weight="bold" /> Add Line
        </button>
      </div>

      {showCats && (
        <CategoryEditor event={event} categories={data.categories} onClose={() => setShowCats(false)} onSaved={load} />
      )}

      {showExpense && (
        <AddExpenseForm event={event} categories={enabledCats} lines={data.lines}
          onCancel={() => setShowExpense(false)}
          onSaved={() => { setShowExpense(false); load(); }} />
      )}

      {showAdd && (
        <LineForm event={event} categories={enabledCats} onCancel={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); load(); }} />
      )}

      {data.lines.length === 0 ? (
        <div className="glass-card p-8 text-center" data-testid="budget-empty">
          <p className="text-sm text-neutral-400">No budget lines yet. Click "Add Line" to plan your spend.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="budget-lines">
          {data.lines.map(ln => (
            <BudgetRow key={ln.line_id} line={ln} event={event}
              onEdit={() => setEditing(ln)}
              onDelete={() => handleDelete(ln.line_id)}
              onSaved={load} />
          ))}
        </div>
      )}

      {editing && (
        <LineForm event={event} categories={enabledCats} initial={editing} isEdit
          onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function BudgetRow({ line, event, onEdit, onDelete, onSaved }) {
  const { api } = useAuth();
  const planned = Number(line.planned_amount || 0);
  const advance = Number(line.advance_paid ?? line.actual_amount ?? 0);
  const hasFinal = line.has_final;
  const finalAmt = hasFinal ? Number(line.final_amount || 0) : null;
  const balance = hasFinal ? (finalAmt - advance) : 0;
  const variation = hasFinal ? (finalAmt - planned) : 0;

  // Progress shown vs the bigger of planned/final
  const denom = hasFinal ? Math.max(planned, finalAmt) : planned;
  const pct = denom > 0 ? Math.min(100, Math.round(advance * 100 / denom)) : 0;
  const overBudget = hasFinal && finalAmt > planned;

  const [editingFinal, setEditingFinal] = useState(false);
  const [finalInput, setFinalInput] = useState(finalAmt !== null ? String(finalAmt) : '');
  const [quickPay, setQuickPay] = useState(false);
  const [payAmt, setPayAmt] = useState('');
  const [payDesc, setPayDesc] = useState('');
  const [saving, setSaving] = useState(false);

  // Quick "Pay full balance" preset
  const fillBalance = () => setPayAmt(balance > 0 ? String(Math.round(balance)) : '');

  const handleSaveFinal = async () => {
    setSaving(true);
    try {
      const val = finalInput === '' ? null : parseFloat(finalInput);
      await api.put(`/events/${event.event_id}/money/budget/${line.line_id}/final`,
        { final_amount: val });
      setEditingFinal(false);
      onSaved && onSaved();
    } catch (e) {
      alert('Failed to save');
    }
    setSaving(false);
  };

  const handleClearFinal = async () => {
    setSaving(true);
    try {
      await api.put(`/events/${event.event_id}/money/budget/${line.line_id}/final`, { final_amount: null });
      setFinalInput('');
      setEditingFinal(false);
      onSaved && onSaved();
    } catch (e) { /* silent */ }
    setSaving(false);
  };

  const handleQuickPay = async () => {
    const amt = parseFloat(payAmt);
    if (!amt || amt <= 0) return;
    setSaving(true);
    try {
      await api.post('/cashbook/transactions', {
        type: 'debit',
        amount: amt,
        category: line.category,
        description: payDesc || `${line.description || line.category} payment`,
        payment_mode: 'UPI',
        date: new Date().toISOString().slice(0, 10),
        event_id: event.event_id,
        event_line_id: line.line_id,
      });
      setQuickPay(false);
      setPayAmt('');
      setPayDesc('');
      onSaved && onSaved();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to book payment');
    }
    setSaving(false);
  };

  return (
    <div className="glass-card p-3.5" data-testid={`budget-line-${line.line_id}`}>
      {/* Row 1: title + actions */}
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-white truncate">{line.description || line.category}</div>
          <div className="text-[10px] uppercase tracking-wider text-neutral-500 mt-0.5">{line.category}</div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={() => { setQuickPay(s => !s); setEditingFinal(false); }}
            data-testid={`budget-pay-${line.line_id}`}
            title="Book a payment toward this line"
            className={`px-2.5 py-1.5 rounded-lg text-[11px] font-bold flex items-center gap-1 ${quickPay ? 'bg-green-500 text-white' : 'bg-green-500/15 text-green-400 hover:bg-green-500/25 border border-green-500/30'}`}>
            <Plus size={11} weight="bold" /> Pay
          </button>
          <button onClick={() => { setEditingFinal(s => !s); setQuickPay(false); }}
            data-testid={`budget-settle-${line.line_id}`}
            title="Enter the final vendor bill — closes out this line"
            className={`px-2.5 py-1.5 rounded-lg text-[11px] font-bold ${editingFinal || hasFinal ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40' : 'bg-white/5 text-neutral-400 hover:text-white border border-white/10'}`}>
            {hasFinal ? '✓ Final set' : 'Set Final'}
          </button>
          <button onClick={onEdit} data-testid={`budget-edit-${line.line_id}`}
            className="p-1.5 rounded-lg text-neutral-500 hover:text-white hover:bg-white/5">
            <PencilSimple size={13} />
          </button>
          <button onClick={onDelete} data-testid={`budget-delete-${line.line_id}`}
            className="p-1.5 rounded-lg text-red-400 hover:bg-red-500/10">
            <Trash size={13} />
          </button>
        </div>
      </div>

      {/* Row 2: 5 KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-2.5">
        <Pill label="Budget" value={formatINR(planned)} tone="neutral" />
        <Pill label="Advance" value={formatINR(advance)} tone="green" />
        <Pill label="Final" value={hasFinal ? formatINR(finalAmt) : '—'} tone="amber" muted={!hasFinal} />
        <Pill label="Balance" value={hasFinal ? formatINR(Math.abs(balance)) : '—'}
          tone={balance > 0 ? 'red' : balance < 0 ? 'green' : 'neutral'}
          muted={!hasFinal}
          suffix={hasFinal && balance < 0 ? 'refund' : hasFinal && balance > 0 ? 'to pay' : ''} />
        <Pill label="Variation"
          value={hasFinal ? (variation === 0 ? '₹0' : (variation > 0 ? '+' : '-') + formatINR(Math.abs(variation))) : '—'}
          tone={variation > 0 ? 'red' : variation < 0 ? 'green' : 'neutral'}
          muted={!hasFinal}
          suffix={hasFinal && variation > 0 ? 'over' : hasFinal && variation < 0 ? 'under' : ''} />
      </div>

      {/* Progress bar (advance vs the bigger of planned/final) */}
      {denom > 0 && (
        <div className="w-full h-1.5 rounded-full bg-white/5 overflow-hidden mt-2.5">
          <div className="h-full rounded-full transition-all"
            style={{ width: `${pct}%`, background: overBudget ? '#EF4444' : pct >= 100 ? '#22C55E' : pct > 80 ? '#F59E0B' : '#22C55E' }} />
        </div>
      )}

      {/* Inline Quick-Pay form (one row) */}
      {quickPay && (
        <div className="mt-3 pt-3 border-t border-white/5 flex items-center gap-2 flex-wrap" data-testid={`budget-quick-pay-${line.line_id}`}>
          <span className="text-[10px] uppercase tracking-widest text-green-400 font-bold mr-1">Pay</span>
          <input type="number" placeholder="₹ amount" value={payAmt} onChange={e => setPayAmt(e.target.value)}
            data-testid={`pay-amount-${line.line_id}`} autoFocus
            className="w-28 px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/10 text-sm text-white outline-none focus:border-green-500/50" />
          {balance > 0 && (
            <button onClick={fillBalance} className="text-[10px] text-amber-400 hover:text-amber-300 font-semibold underline">
              Use balance ({formatINR(balance)})
            </button>
          )}
          <input type="text" placeholder="Note (optional)" value={payDesc} onChange={e => setPayDesc(e.target.value)}
            className="flex-1 min-w-[120px] px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/10 text-sm text-white outline-none focus:border-green-500/50" />
          <button onClick={handleQuickPay} disabled={saving || !payAmt}
            data-testid={`pay-confirm-${line.line_id}`}
            className="px-3 py-1.5 rounded-lg bg-green-500 text-white text-xs font-bold disabled:opacity-40">
            {saving ? '…' : 'Book'}
          </button>
          <button onClick={() => { setQuickPay(false); setPayAmt(''); setPayDesc(''); }}
            className="px-2 py-1.5 rounded-lg text-neutral-500 hover:text-white text-xs">
            Cancel
          </button>
        </div>
      )}

      {/* Inline Final-Cost editor */}
      {editingFinal && (
        <div className="mt-3 pt-3 border-t border-white/5 flex items-center gap-2 flex-wrap" data-testid={`budget-final-form-${line.line_id}`}>
          <span className="text-[10px] uppercase tracking-widest text-amber-400 font-bold mr-1">Final vendor bill</span>
          <span className="text-xs text-neutral-500">₹</span>
          <input type="number" value={finalInput} onChange={e => setFinalInput(e.target.value)}
            data-testid={`final-input-${line.line_id}`} autoFocus placeholder="e.g. 16200"
            className="w-32 px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/10 text-sm text-white outline-none focus:border-amber-500/50" />
          <button onClick={handleSaveFinal} disabled={saving}
            data-testid={`final-save-${line.line_id}`}
            className="px-3 py-1.5 rounded-lg bg-amber-500 text-white text-xs font-bold disabled:opacity-40">
            {saving ? '…' : 'Save'}
          </button>
          {hasFinal && (
            <button onClick={handleClearFinal} disabled={saving}
              className="px-3 py-1.5 rounded-lg border border-red-500/30 text-red-400 text-xs hover:bg-red-500/10">
              Un-settle
            </button>
          )}
          <button onClick={() => { setEditingFinal(false); setFinalInput(hasFinal ? String(finalAmt) : ''); }}
            className="px-2 py-1.5 rounded-lg text-neutral-500 hover:text-white text-xs">
            Cancel
          </button>
          <span className="text-[10px] text-neutral-500 ml-auto">
            Once set, Balance & Variation are calculated automatically.
          </span>
        </div>
      )}

      {line.notes && !quickPay && !editingFinal && (
        <p className="text-[11px] text-neutral-500 mt-2 italic">{line.notes}</p>
      )}
    </div>
  );
}

// Compact KPI pill used inside the budget row.
function Pill({ label, value, tone = 'neutral', muted = false, suffix = '' }) {
  const toneMap = {
    neutral: { bg: 'rgba(255,255,255,0.04)', border: 'rgba(255,255,255,0.08)', color: '#E5E5E5' },
    green:   { bg: 'rgba(34,197,94,0.10)',   border: 'rgba(34,197,94,0.30)',   color: '#4ADE80' },
    amber:   { bg: 'rgba(245,158,11,0.10)',  border: 'rgba(245,158,11,0.30)',  color: '#FBBF24' },
    red:     { bg: 'rgba(239,68,68,0.10)',   border: 'rgba(239,68,68,0.30)',   color: '#F87171' },
  };
  const t = toneMap[tone] || toneMap.neutral;
  return (
    <div className="rounded-lg px-2 py-1.5" style={{ background: muted ? 'rgba(255,255,255,0.03)' : t.bg, border: `1px solid ${muted ? 'rgba(255,255,255,0.06)' : t.border}` }}>
      <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-semibold">{label}</div>
      <div className="flex items-baseline gap-1 mt-0.5">
        <span className="text-[13px] font-bold leading-tight" style={{ color: muted ? '#737373' : t.color }}>{value}</span>
        {suffix && <span className="text-[9px] uppercase tracking-wider" style={{ color: t.color, opacity: 0.7 }}>{suffix}</span>}
      </div>
    </div>
  );
}

function LineForm({ event, categories, initial, isEdit, onCancel, onSaved }) {
  const { api } = useAuth();
  const [form, setForm] = useState({
    category: initial?.category || categories[0] || 'Miscellaneous',
    description: initial?.description || '',
    planned_amount: initial?.planned_amount ?? '',
    notes: initial?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        category: form.category,
        description: form.description,
        planned_amount: parseFloat(form.planned_amount) || 0,
        notes: form.notes,
      };
      if (isEdit) {
        await api.put(`/events/${event.event_id}/money/budget/${initial.line_id}`, payload);
      } else {
        await api.post(`/events/${event.event_id}/money/budget`, payload);
      }
      onSaved();
    } catch (e) { alert('Save failed: ' + (e.response?.data?.detail || e.message)); }
    setSaving(false);
  };

  return (
    <div className="glass-card p-4 border border-[#FF6B35]/30" data-testid="budget-line-form">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <FormField label="Category">
          <select value={form.category} onChange={(e) => upd('category', e.target.value)} data-testid="line-category"
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white">
            {categories.map(c => <option key={c} value={c} className="bg-neutral-900">{c}</option>)}
            <option value="Custom" className="bg-neutral-900">+ Custom...</option>
          </select>
        </FormField>
        <FormField label="Description">
          <input value={form.description} onChange={(e) => upd('description', e.target.value)}
            data-testid="line-description" placeholder="e.g. Pizza + cake"
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white" />
        </FormField>
        <FormField label="Planned Amount (₹)">
          <input type="number" value={form.planned_amount} onChange={(e) => upd('planned_amount', e.target.value)}
            data-testid="line-amount" placeholder="0"
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white" />
        </FormField>
        <FormField label="Notes (optional)">
          <input value={form.notes} onChange={(e) => upd('notes', e.target.value)}
            data-testid="line-notes" placeholder="Vendor name, contact..."
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white" />
        </FormField>
      </div>
      <div className="flex gap-2 mt-3 justify-end">
        <button onClick={onCancel} className="px-4 py-2 rounded-full border border-white/10 text-xs font-medium hover:bg-white/5">Cancel</button>
        <button onClick={save} disabled={saving} data-testid="line-save-btn"
          className="px-4 py-2 rounded-full bg-[#FF6B35] text-white text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50">
          <FloppyDisk size={14} /> {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
}

function CategoryEditor({ event, categories, onClose, onSaved }) {
  const { api } = useAuth();
  const [list, setList] = useState(categories);
  const [newCat, setNewCat] = useState('');
  const toggle = (i) => setList(prev => prev.map((c, idx) => idx === i ? { ...c, enabled: !c.enabled } : c));
  const remove = (i) => setList(prev => prev.filter((_, idx) => idx !== i));
  const add = () => {
    const nm = newCat.trim();
    if (!nm) return;
    if (list.some(c => c.name.toLowerCase() === nm.toLowerCase())) return;
    setList(prev => [...prev, { name: nm, enabled: true }]);
    setNewCat('');
  };
  const save = async () => {
    await api.put(`/events/${event.event_id}/money/categories`, { categories: list });
    onSaved();
    onClose();
  };

  return (
    <div className="glass-card p-4 border border-white/10" data-testid="category-editor">
      <h4 className="font-heading text-sm font-bold mb-3">Edit Categories</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
        {list.map((c, i) => (
          <div key={c.name + i} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5">
            <button onClick={() => toggle(i)} className="shrink-0">
              {c.enabled
                ? <ToggleRight size={20} weight="fill" className="text-[#FF6B35]" />
                : <ToggleLeft size={20} className="text-neutral-500" />}
            </button>
            <span className={`flex-1 text-xs ${c.enabled ? 'text-white' : 'text-neutral-500 line-through'}`}>{c.name}</span>
            <button onClick={() => remove(i)} className="text-neutral-500 hover:text-red-400">
              <Trash size={12} />
            </button>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <input value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="Custom category name"
          onKeyDown={(e) => e.key === 'Enter' && add()}
          className="flex-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white" />
        <button onClick={add} className="px-3 py-2 rounded-lg border border-white/10 text-xs font-medium hover:bg-white/5">Add</button>
      </div>
      <div className="flex gap-2 mt-3 justify-end">
        <button onClick={onClose} className="px-4 py-2 rounded-full border border-white/10 text-xs font-medium hover:bg-white/5">Cancel</button>
        <button onClick={save} className="px-4 py-2 rounded-full bg-[#FF6B35] text-white text-xs font-semibold">Save</button>
      </div>
    </div>
  );
}

// ------------------------------------------------------------
// Shagun
// ------------------------------------------------------------
function ShagunView({ event }) {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showBulk, setShowBulk] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/events/${event.event_id}/money/shagun`);
      setData(r.data);
    } catch (e) { console.error(e); }
  }, [api, event.event_id]);

  useEffect(() => { load(); }, [load]);

  const toggleThank = async (id) => {
    await api.put(`/events/${event.event_id}/money/shagun/${id}/thank`);
    load();
  };
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this shagun entry?')) return;
    await api.delete(`/events/${event.event_id}/money/shagun/${id}`);
    load();
  };
  const sendThanks = (item) => {
    const phone = (item.phone || '').replace(/[^\d+]/g, '');
    const msg = `Dear ${item.giver_name}, thank you so much for your blessings and the lovely gift on the occasion of ${event.title}. It truly meant a lot to us. — Warm regards.`;
    const url = phone ? `https://wa.me/${phone}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(url, '_blank');
  };

  if (!data) return <Spinner />;
  const pendingThanks = data.items.filter(i => !i.thanked).length;

  return (
    <div className="space-y-3">
      <div className="glass-card p-4 flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <h3 className="font-heading text-sm font-bold">Shagun Ledger</h3>
          <p className="text-xs text-neutral-400 mt-1">
            Track cash gifts received. {pendingThanks > 0 && <span className="text-yellow-400">{pendingThanks} thank-you pending.</span>}
          </p>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-neutral-500">Total Received</div>
          <div className="text-base font-bold text-green-400">{formatINR(data.total_amount)}</div>
        </div>
        <button onClick={() => setShowBulk(s => !s)} data-testid="shagun-bulk-btn"
          className="px-3 py-2 rounded-full border border-white/10 text-xs font-medium hover:bg-white/5 flex items-center gap-1.5">
          <ListBullets size={14} /> {showBulk ? 'Hide Bulk' : 'Bulk Paste'}
        </button>
        <button onClick={() => setShowAdd(true)} data-testid="shagun-add-btn"
          className="px-4 py-2 rounded-full bg-[#FF6B35] text-white text-xs font-semibold flex items-center gap-1.5 hover:opacity-90">
          <Plus size={14} weight="bold" /> Add Entry
        </button>
      </div>

      {showBulk && <ShagunBulkPaste event={event} onClose={() => setShowBulk(false)} onSaved={load} />}
      {showAdd && <ShagunForm event={event} onCancel={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}

      {data.items.length === 0 ? (
        <div className="glass-card p-8 text-center" data-testid="shagun-empty">
          <Coins size={32} className="mx-auto text-neutral-500 mb-2" />
          <p className="text-sm text-neutral-400">No shagun entries yet. Add as guests give.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="shagun-list">
          {data.items.map(s => (
            <div key={s.shagun_id} data-testid={`shagun-${s.shagun_id}`}
              className="glass-card p-3 flex items-center gap-3 flex-wrap">
              <div className="w-9 h-9 rounded-xl bg-green-500/15 flex items-center justify-center shrink-0">
                <span className="text-[11px] font-bold text-green-400">
                  {(s.giver_name || '?').slice(0, 1).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-[140px]">
                <div className="text-sm font-semibold text-white">{s.giver_name}</div>
                <div className="text-[11px] text-neutral-400 flex items-center gap-2 flex-wrap">
                  <span>{s.relation}</span>
                  <span>•</span>
                  <span>{s.payment_mode}</span>
                  {s.note && <><span>•</span><span className="italic">{s.note}</span></>}
                </div>
              </div>
              <div className="text-right">
                <div className="text-base font-bold text-green-400">{formatINR(s.amount)}</div>
              </div>
              <button onClick={() => toggleThank(s.shagun_id)} data-testid={`shagun-thank-${s.shagun_id}`}
                title={s.thanked ? 'Marked as thanked' : 'Mark as thanked'}
                className={`p-2 rounded-lg border ${s.thanked ? 'border-green-500/30 bg-green-500/10 text-green-400' : 'border-white/10 text-neutral-500 hover:text-white'}`}>
                <Check size={14} weight={s.thanked ? 'bold' : 'regular'} />
              </button>
              <button onClick={() => sendThanks(s)} data-testid={`shagun-send-${s.shagun_id}`}
                title="Send thank-you WhatsApp"
                className="p-2 rounded-lg border border-[#25D366]/30 text-[#25D366] hover:bg-[#25D366]/10">
                <WhatsappLogo size={14} />
              </button>
              <button onClick={() => handleDelete(s.shagun_id)} data-testid={`shagun-delete-${s.shagun_id}`}
                className="p-2 rounded-lg border border-red-500/30 text-red-400 hover:bg-red-500/10">
                <Trash size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ShagunForm({ event, onCancel, onSaved }) {
  const { api } = useAuth();
  const [form, setForm] = useState({
    giver_name: '', amount: '', relation: 'Father side', payment_mode: 'Cash', note: '',
  });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.giver_name.trim() || !form.amount) return;
    setSaving(true);
    try {
      await api.post(`/events/${event.event_id}/money/shagun`, {
        ...form,
        amount: parseFloat(form.amount) || 0,
      });
      onSaved();
    } catch (e) { alert('Save failed: ' + (e.response?.data?.detail || e.message)); }
    setSaving(false);
  };

  return (
    <div className="glass-card p-4 border border-[#FF6B35]/30" data-testid="shagun-form">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        <FormField label="Giver Name">
          <input value={form.giver_name} onChange={(e) => upd('giver_name', e.target.value)}
            data-testid="sh-name" placeholder="e.g. Uncle Raj"
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white" />
        </FormField>
        <FormField label="Amount (₹)">
          <input type="number" value={form.amount} onChange={(e) => upd('amount', e.target.value)}
            data-testid="sh-amount" placeholder="0"
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white" />
        </FormField>
        <FormField label="Relation">
          <select value={form.relation} onChange={(e) => upd('relation', e.target.value)} data-testid="sh-relation"
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white">
            {RELATION_OPTIONS.map(r => <option key={r} value={r} className="bg-neutral-900">{r}</option>)}
          </select>
        </FormField>
        <FormField label="Mode">
          <select value={form.payment_mode} onChange={(e) => upd('payment_mode', e.target.value)} data-testid="sh-mode"
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white">
            {PAYMENT_MODES.map(p => <option key={p} value={p} className="bg-neutral-900">{p}</option>)}
          </select>
        </FormField>
        <FormField label="Note (optional)">
          <input value={form.note} onChange={(e) => upd('note', e.target.value)}
            data-testid="sh-note" placeholder=""
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white" />
        </FormField>
      </div>
      <div className="flex gap-2 mt-3 justify-end">
        <button onClick={onCancel} className="px-4 py-2 rounded-full border border-white/10 text-xs font-medium hover:bg-white/5">Cancel</button>
        <button onClick={save} disabled={saving || !form.giver_name.trim() || !form.amount} data-testid="sh-save"
          className="px-4 py-2 rounded-full bg-[#FF6B35] text-white text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50">
          <FloppyDisk size={14} /> {saving ? 'Saving...' : 'Add Shagun'}
        </button>
      </div>
    </div>
  );
}

function ShagunBulkPaste({ event, onClose, onSaved }) {
  const { api } = useAuth();
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const save = async () => {
    if (!text.trim()) return;
    setSaving(true);
    setResult(null);
    try {
      const r = await api.post(`/events/${event.event_id}/money/shagun/bulk`, { text });
      setResult(r.data);
      if (r.data.added > 0) { setText(''); onSaved(); }
    } catch (e) { alert('Bulk save failed: ' + (e.response?.data?.detail || e.message)); }
    setSaving(false);
  };

  return (
    <div className="glass-card p-4 border border-[#FF6B35]/30" data-testid="shagun-bulk-form">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-semibold text-white flex items-center gap-1.5">
          <Sparkle size={14} className="text-[#FF6B35]" /> Bulk Paste
        </h4>
        <button onClick={onClose} className="text-neutral-500 hover:text-white">
          <X size={14} />
        </button>
      </div>
      <p className="text-[11px] text-neutral-400 mb-2">
        One per line. Format: <code className="text-[#FF6B35]">Name Amount Relation</code> — relation optional.<br />
        Example: <code className="text-neutral-300">Uncle Raj 1100 Father side</code> · <code className="text-neutral-300">Sharma Family 5100</code>
      </p>
      <textarea value={text} onChange={(e) => setText(e.target.value)}
        data-testid="bulk-text" rows={5}
        placeholder={'Anita Aunty 2100 Mother side\nSharma Family 5100 Relative\nDelhi Cousin 1100'}
        className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white font-mono" />
      {result && (
        <p className="text-xs text-neutral-300 mt-2">
          ✓ Added <span className="text-green-400 font-bold">{result.added}</span>
          {result.skipped > 0 && <> · Skipped <span className="text-red-400 font-bold">{result.skipped}</span></>}
        </p>
      )}
      <div className="flex gap-2 mt-3 justify-end">
        <button onClick={save} disabled={saving || !text.trim()} data-testid="bulk-save-btn"
          className="px-4 py-2 rounded-full bg-[#FF6B35] text-white text-xs font-semibold disabled:opacity-50">
          {saving ? 'Saving...' : 'Save All'}
        </button>
      </div>
    </div>
  );
}

// ------------------------------------------------------------
// Shared
// ------------------------------------------------------------
function KPI({ label, value, color, sub, subColor }) {
  return (
    <div className="glass-card p-4">
      <div className="text-[10px] uppercase tracking-wider text-neutral-500">{label}</div>
      <div className="text-xl font-extrabold mt-1" style={{ color }}>{value}</div>
      {sub && <div className="text-[10px] mt-0.5" style={{ color: subColor || '#a3a3a3' }}>{sub}</div>}
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider text-neutral-500 block mb-1">{label}</span>
      {children}
    </label>
  );
}

function Spinner() {
  return <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" /></div>;
}


// ------------------------------------------------------------
// AddExpenseForm — books a real expense (creates a Cashbook debit
// auto-tagged with this event's event_id so the actual updates live).
// ------------------------------------------------------------
const PAYMENT_MODES_EXP = ['UPI', 'Cash', 'Bank Transfer', 'Cheque', 'Card'];

function AddExpenseForm({ event, categories, lines = [], onCancel, onSaved }) {
  const { api } = useAuth();
  const [form, setForm] = useState({
    amount: '', category: '', description: '', payment_mode: 'UPI',
    reference: '', date: new Date().toISOString().slice(0, 10),
  });
  // Budget-line dropdown value. Format:
  //   "line:<line_id>"  → existing budget line
  //   "cat:<name>"      → raw category (no line yet)
  //   ""                → not selected
  const [selection, setSelection] = useState('');
  const [saving, setSaving] = useState(false);

  // When a line is picked, prefill the description with the budget line's description
  const onSelectionChange = (val) => {
    setSelection(val);
    if (val.startsWith('line:')) {
      const lineId = val.slice(5);
      const line = lines.find(l => l.line_id === lineId);
      if (line) {
        setForm(f => ({
          ...f,
          category: line.category || '',
          description: f.description || line.description || '',
        }));
      }
    } else if (val.startsWith('cat:')) {
      setForm(f => ({ ...f, category: val.slice(4) }));
    } else {
      setForm(f => ({ ...f, category: '' }));
    }
  };

  // Categories not yet covered by a budget line
  const linedCats = new Set(lines.map(l => l.category));
  const orphanCats = (categories || []).filter(c => !linedCats.has(c));

  const handleSave = async () => {
    if (!form.amount || !form.category) {
      alert('Amount and budget line are required.');
      return;
    }
    setSaving(true);
    try {
      await api.post('/cashbook/transactions', {
        type: 'debit',
        amount: parseFloat(form.amount),
        category: form.category,
        description: form.description,
        payment_mode: form.payment_mode,
        reference: form.reference,
        date: form.date,
        event_id: event.event_id,
      });
      onSaved();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to book expense');
    }
    setSaving(false);
  };

  // Pick which budget line this expense will roll up under in the live actual.
  // Picking a "line" pre-fills category from that line. Picking a "category"
  // means the spend rolls up to that category in the breakdown but no specific
  // line — show a hint that the user can add a budget line later.
  const selectedLine = selection.startsWith('line:')
    ? lines.find(l => l.line_id === selection.slice(5))
    : null;
  const remainingOnLine = selectedLine
    ? (selectedLine.planned_amount - selectedLine.actual_amount)
    : null;

  // Native <select> dark mode fix: set colorScheme + black option text so
  // the OS-rendered popup uses dark background (Chromium honors colorScheme)
  // and the per-option `style` ensures fallback visibility.
  const darkSelectStyle = { colorScheme: 'dark' };

  return (
    <div className="glass-card p-4" data-testid="add-expense-form"
      style={{ borderColor: '#22C55E40', borderWidth: 1 }}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <h4 className="font-heading text-sm font-bold text-white">Book an Expense</h4>
          <p className="text-[10px] text-neutral-500 mt-0.5">
            Creates a Cashbook debit auto-tagged to {event.theme_emoji || '🎉'} <span className="text-neutral-300">{event.title}</span>
          </p>
        </div>
        <button onClick={onCancel} className="text-neutral-500 hover:text-white"><X size={16} /></button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FormField label="Amount (₹) *">
          <input type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
            data-testid="expense-amount" autoFocus placeholder="5000"
            className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none focus:border-green-500/50" />
        </FormField>
        <FormField label="Book against budget line *">
          <select value={selection} onChange={e => onSelectionChange(e.target.value)}
            data-testid="expense-category" style={darkSelectStyle}
            className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none focus:border-green-500/50">
            <option value="" style={{ color: '#000' }}>— Select a budget line —</option>
            {lines.length > 0 && (
              <optgroup label="Budget lines (recommended)" style={{ color: '#000' }}>
                {lines.map(l => {
                  const left = (l.planned_amount || 0) - (l.actual_amount || 0);
                  const label = `${l.category} · ${l.description || '(no description)'} · ${formatINR(l.planned_amount)} planned`
                    + (left < 0 ? ` · ${formatINR(-left)} OVER` : ` · ${formatINR(left)} left`);
                  return (
                    <option key={l.line_id} value={`line:${l.line_id}`} style={{ color: '#000' }}>
                      {label}
                    </option>
                  );
                })}
              </optgroup>
            )}
            {orphanCats.length > 0 && (
              <optgroup label="Other categories (no budget line yet)" style={{ color: '#000' }}>
                {orphanCats.map(c => (
                  <option key={c} value={`cat:${c}`} style={{ color: '#000' }}>{c}</option>
                ))}
              </optgroup>
            )}
          </select>
        </FormField>
        <FormField label="Description / Vendor">
          <input type="text" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            data-testid="expense-description" placeholder="e.g. Pizza Hut, Decorator advance"
            className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none focus:border-green-500/50" />
        </FormField>
        <FormField label="Payment Mode">
          <select value={form.payment_mode} onChange={e => setForm(f => ({ ...f, payment_mode: e.target.value }))}
            style={darkSelectStyle}
            className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none focus:border-green-500/50">
            {PAYMENT_MODES_EXP.map(m => <option key={m} value={m} style={{ color: '#000' }}>{m}</option>)}
          </select>
        </FormField>
        <FormField label="Date">
          <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
            style={darkSelectStyle}
            className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none focus:border-green-500/50" />
        </FormField>
        <FormField label="Reference (optional)">
          <input type="text" value={form.reference} onChange={e => setForm(f => ({ ...f, reference: e.target.value }))}
            placeholder="UPI ref, invoice no."
            className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none focus:border-green-500/50" />
        </FormField>
      </div>

      {/* Selection hint */}
      {selectedLine && (
        <div className="mt-3 px-3 py-2 rounded-xl text-[11px]"
          style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)', color: '#86efac' }}>
          ✓ This expense will roll up to <b>{selectedLine.description || selectedLine.category}</b>
          {remainingOnLine !== null && (
            <span className="text-neutral-400">
              {' '}· {remainingOnLine >= 0
                ? <>still <b className="text-green-400">{formatINR(remainingOnLine)}</b> available on this line</>
                : <>already <b className="text-red-400">{formatINR(-remainingOnLine)} over</b> the planned amount</>}
            </span>
          )}
        </div>
      )}
      {selection.startsWith('cat:') && (
        <div className="mt-3 px-3 py-2 rounded-xl text-[11px]"
          style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', color: '#FBBF24' }}>
          ⚠ No budget line exists yet for <b>{selection.slice(4)}</b>. The spend will appear in this category's actual on the Summary, but consider adding a budget line for full Plan-vs-Actual tracking.
        </div>
      )}

      <div className="flex items-center justify-end gap-2 mt-4">
        <button onClick={onCancel} className="px-4 py-2 rounded-full text-xs font-semibold text-neutral-400 hover:text-white">
          Cancel
        </button>
        <button onClick={handleSave} disabled={saving || !selection} data-testid="expense-save-btn"
          className="px-5 py-2 rounded-full bg-green-500 text-white text-xs font-bold hover:opacity-90 disabled:opacity-50 flex items-center gap-1.5">
          <Check size={14} weight="bold" /> {saving ? 'Booking…' : 'Book Expense'}
        </button>
      </div>
    </div>
  );
}

// ------------------------------------------------------------
// ShareSheetModal — generate / copy / revoke a read-only public link.
// ------------------------------------------------------------
const DEFAULT_VIS = {
  show_budget: true,
  show_expenses: true,
  hide_expense_descriptions: false,
  show_shagun: true,
  hide_shagun_names: false,
  show_shagun_notes: true,
  show_variation: true,
  show_summary: true,
};

const VIS_TOGGLES = [
  { key: 'show_summary',              label: 'Show Summary tab',                  hint: 'Totals + per-category breakdown',                            invert: false },
  { key: 'show_budget',               label: 'Show Budget tab',                   hint: 'Per-line planned / advance / final',                         invert: false },
  { key: 'show_variation',            label: 'Show Variation & Balance Due',      hint: 'Hide if you don\'t want viewers seeing over-budget amounts',  invert: false },
  { key: 'show_expenses',             label: 'Show Expenses tab',                 hint: 'Each cashbook debit, with date / mode',                      invert: false },
  { key: 'hide_expense_descriptions', label: 'Hide expense vendor names',         hint: 'Show only category + amount',                                invert: true  },
  { key: 'show_shagun',               label: 'Show Shagun tab',                   hint: 'Gift envelopes received',                                    invert: false },
  { key: 'hide_shagun_names',         label: 'Hide shagun giver names',           hint: 'Replace names with "Anonymous" — relation + amount still shown', invert: true },
  { key: 'show_shagun_notes',         label: 'Show shagun notes',                 hint: 'Personal messages attached to gifts',                        invert: false },
];

// Returns { days, label, tone } describing how soon a share expires.
// Tone is used to color the badge on the share chip + details panel.
function expiryStatus(expiresAtISO) {
  if (!expiresAtISO) return null;
  const exp = new Date(expiresAtISO);
  if (isNaN(exp.getTime())) return null;
  const now = new Date();
  const ms = exp - now;
  const days = Math.ceil(ms / (1000 * 60 * 60 * 24));
  if (ms <= 0) return { days, label: 'expired', tone: 'red', expired: true };
  if (days <= 1) return { days, label: 'expires today', tone: 'red' };
  if (days <= 7) return { days, label: `${days}d left`, tone: 'amber' };
  if (days <= 30) return { days, label: `${days}d left`, tone: 'neutral' };
  return { days, label: `${days}d left`, tone: 'green' };
}

function ShareSheetModal({ event, onClose }) {
  const { api } = useAuth();
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newName, setNewName] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/events/${event.event_id}/money/shares`);
      const list = r.data?.shares || [];
      setShares(list);
      if (!activeId && list.length) setActiveId(list[0].share_id);
      if (!list.length) setShowNewForm(true);
    } catch (e) { setShares([]); }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const handleCreate = async () => {
    const name = (newName || '').trim() || 'New share';
    setLoading(true);
    try {
      const r = await api.post(`/events/${event.event_id}/money/shares`, { name });
      setNewName('');
      setShowNewForm(false);
      await load();
      setActiveId(r.data.share_id);
    } catch (e) { alert('Could not create share.'); }
    setLoading(false);
  };

  const active = shares.find(s => s.share_id === activeId);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start sm:items-center justify-center p-3 sm:p-4 overflow-y-auto"
      data-testid="share-sheet-modal" onClick={onClose}>
      <div className="glass-card max-w-2xl w-full p-5 my-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ShareNetwork size={18} className="text-purple-400" />
            <div>
              <h3 className="font-heading text-base font-bold">Share Money Sheet</h3>
              <p className="text-[10px] text-neutral-500 mt-0.5">Create multiple named views for different audiences</p>
            </div>
          </div>
          <button onClick={onClose} className="text-neutral-500 hover:text-white"><X size={18} /></button>
        </div>

        {loading && shares.length === 0 ? (
          <Spinner />
        ) : (
          <>
            {/* Share tabs / chips */}
            {shares.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid="share-tabs">
                {shares.map(s => {
                  const exp = expiryStatus(s.expires_at);
                  const expBg = exp ? ({
                    red: 'bg-red-500/20 text-red-300',
                    amber: 'bg-amber-500/20 text-amber-300',
                    neutral: 'bg-white/10 text-neutral-300',
                    green: 'bg-green-500/15 text-green-300',
                  }[exp.tone] || 'bg-white/10 text-neutral-300') : '';
                  return (
                  <button key={s.share_id} onClick={() => { setActiveId(s.share_id); setShowNewForm(false); }}
                    data-testid={`share-tab-${s.share_id}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 transition ${activeId === s.share_id && !showNewForm ? 'bg-purple-500/25 text-purple-300 border border-purple-500/40' : 'bg-white/5 text-neutral-400 hover:text-white border border-white/10'}`}>
                    {s.has_password && <span title="Password-protected">🔒</span>}
                    {s.name}
                    {!s.enabled && <span className="text-[9px] text-neutral-500">(off)</span>}
                    {exp && (
                      <span className={`text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded ${expBg}`}
                        title={s.expires_at}>
                        {exp.label}
                      </span>
                    )}
                  </button>
                  );
                })}
                <button onClick={() => { setShowNewForm(true); setActiveId(null); }}
                  data-testid="share-add-btn"
                  className={`px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1 transition ${showNewForm ? 'bg-green-500/25 text-green-300 border border-green-500/40' : 'bg-white/5 text-green-400 hover:bg-green-500/10 border border-green-500/30'}`}>
                  <Plus size={11} weight="bold" /> New
                </button>
              </div>
            )}

            {/* New share form */}
            {showNewForm && (
              <div className="rounded-2xl p-3 mb-3" style={{ background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.2)' }}>
                <div className="text-[10px] uppercase tracking-widest text-green-400 font-bold mb-1.5">Create a new share profile</div>
                <div className="flex items-center gap-2 flex-wrap">
                  <input type="text" value={newName} onChange={e => setNewName(e.target.value)}
                    placeholder="e.g. Accountant view, WhatsApp family"
                    data-testid="new-share-name"
                    autoFocus
                    className="flex-1 min-w-[180px] px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none focus:border-green-500/50" />
                  <button onClick={handleCreate}
                    data-testid="new-share-create"
                    className="px-4 py-2 rounded-xl bg-green-500 text-white text-xs font-bold">
                    Create
                  </button>
                </div>
              </div>
            )}

            {/* Active share details */}
            {active && !showNewForm && (
              <ShareDetails event={event} share={active} onChanged={load} api={api} />
            )}
          </>
        )}
      </div>
    </div>
  );
}

function ShareDetails({ event, share, onChanged, api }) {
  const [name, setName] = useState(share.name);
  const [vis, setVis] = useState({ ...DEFAULT_VIS, ...(share.visibility || {}) });
  const [hasPassword, setHasPassword] = useState(!!share.has_password);
  const [pwInput, setPwInput] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [expiresAt, setExpiresAt] = useState(share.expires_at ? share.expires_at.slice(0, 10) : '');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  // Sync when switching between share tabs
  useEffect(() => {
    setName(share.name);
    setVis({ ...DEFAULT_VIS, ...(share.visibility || {}) });
    setHasPassword(!!share.has_password);
    setPwInput('');
    setShowPw(false);
    setExpiresAt(share.expires_at ? share.expires_at.slice(0, 10) : '');
    setDirty(false);
    // eslint-disable-next-line
  }, [share.share_id]);

  const url = `${window.location.origin}/party-sheet/${share.token}`;

  const toggleVis = (k, invert) => {
    setVis(v => ({ ...v, [k]: invert ? !v[k] : !v[k] }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const body = { name, visibility: vis };
      if (showPw && pwInput.trim()) body.password = pwInput;
      if (showPw && pwInput === '') body.password = '';
      // Always send expires_at if it's been touched — "" means clear
      body.expires_at = expiresAt || '';
      await api.put(`/events/${event.event_id}/money/shares/${share.share_id}`, body);
      setDirty(false);
      setShowPw(false);
      setPwInput('');
      onChanged && onChanged();
    } catch (e) { alert('Failed to save'); }
    setSaving(false);
  };

  const handleRotate = async () => {
    if (!window.confirm('Rotate the URL? Old link will stop working immediately.')) return;
    setSaving(true);
    try {
      await api.put(`/events/${event.event_id}/money/shares/${share.share_id}`, { rotate_token: true });
      onChanged && onChanged();
    } catch (e) { /* silent */ }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${share.name}" share? This cannot be undone.`)) return;
    setSaving(true);
    try {
      await api.delete(`/events/${event.event_id}/money/shares/${share.share_id}`);
      onChanged && onChanged();
    } catch (e) { /* silent */ }
    setSaving(false);
  };

  const handleToggleEnabled = async () => {
    setSaving(true);
    try {
      await api.put(`/events/${event.event_id}/money/shares/${share.share_id}`, { enabled: !share.enabled });
      onChanged && onChanged();
    } catch (e) { /* silent */ }
    setSaving(false);
  };

  const handleRemovePassword = async () => {
    if (!window.confirm('Remove password protection?')) return;
    setSaving(true);
    try {
      await api.put(`/events/${event.event_id}/money/shares/${share.share_id}`, { password: '' });
      setHasPassword(false);
      onChanged && onChanged();
    } catch (e) { /* silent */ }
    setSaving(false);
  };

  const copy = () => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const waUrl = () => {
    const msg = `Here's the money sheet for ${event.theme_emoji || '🎉'} ${event.title}:\n\n${url}${hasPassword ? '\n\n🔒 Password required (ask host)' : ''}`;
    return `https://wa.me/?text=${encodeURIComponent(msg)}`;
  };

  return (
    <div className="space-y-3" data-testid={`share-details-${share.share_id}`}>
      {/* Name editor */}
      <div className="flex items-center gap-2">
        <input type="text" value={name}
          onChange={e => { setName(e.target.value); setDirty(true); }}
          data-testid="share-name-input"
          className="flex-1 px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm font-bold text-white outline-none focus:border-purple-500/50" />
        <button onClick={handleToggleEnabled}
          title={share.enabled ? 'Click to disable this link' : 'Click to enable this link'}
          className={`px-3 py-2 rounded-xl text-xs font-bold ${share.enabled ? 'bg-green-500/15 text-green-400 border border-green-500/30' : 'bg-neutral-700/30 text-neutral-400 border border-neutral-600/30'}`}>
          {share.enabled ? '● ENABLED' : '○ DISABLED'}
        </button>
        <button onClick={handleDelete} title="Delete this share profile"
          data-testid="share-delete-btn"
          className="p-2 rounded-xl text-red-400 hover:bg-red-500/10 border border-red-500/20">
          <Trash size={14} />
        </button>
      </div>

      {/* Visibility toggles */}
      <div className="rounded-2xl p-3" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="text-[10px] uppercase tracking-widest text-neutral-400 font-bold mb-2">What viewers can see</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-0.5 gap-x-2">
          {VIS_TOGGLES.map(t => {
            const checked = !!vis[t.key];
            const disabled = (t.key === 'hide_shagun_names' && !vis.show_shagun)
              || (t.key === 'show_shagun_notes' && !vis.show_shagun)
              || (t.key === 'hide_expense_descriptions' && !vis.show_expenses)
              || (t.key === 'show_variation' && !vis.show_budget);
            return (
              <label key={t.key}
                className={`flex items-start gap-2 py-1 rounded-md px-1.5 ${disabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-white/5 cursor-pointer'}`}>
                <input type="checkbox" checked={checked} disabled={disabled}
                  onChange={() => !disabled && toggleVis(t.key, t.invert)}
                  data-testid={`vis-toggle-${t.key}`}
                  className="mt-0.5 w-4 h-4 rounded accent-purple-500" />
                <div className="min-w-0 flex-1">
                  <div className={`text-[11px] font-semibold ${t.invert ? 'text-amber-300' : 'text-white'}`}>{t.label}</div>
                </div>
              </label>
            );
          })}
        </div>
      </div>

      {/* Password */}
      <div className="rounded-2xl p-3" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-400 font-bold mb-0.5">Password</div>
            <div className="text-xs text-neutral-300">
              {hasPassword
                ? <>🔒 Currently <b>password-protected</b>. Viewers will see a lock screen.</>
                : <>🔓 Currently open. Anyone with the link can view.</>}
            </div>
          </div>
          {!showPw ? (
            <div className="flex items-center gap-1.5">
              <button onClick={() => setShowPw(true)} data-testid="share-set-password"
                className="px-3 py-1.5 rounded-lg bg-amber-500/15 text-amber-300 border border-amber-500/30 text-xs font-bold">
                {hasPassword ? 'Change' : 'Add password'}
              </button>
              {hasPassword && (
                <button onClick={handleRemovePassword} data-testid="share-remove-password"
                  className="px-2 py-1.5 rounded-lg border border-red-500/30 text-red-400 text-xs font-bold hover:bg-red-500/10">
                  Remove
                </button>
              )}
            </div>
          ) : null}
        </div>
        {showPw && (
          <div className="mt-2 flex items-center gap-2">
            <input type="text" value={pwInput}
              onChange={e => { setPwInput(e.target.value); setDirty(true); }}
              data-testid="share-password-input"
              placeholder="Enter password (e.g. diraparty2026)"
              autoFocus
              className="flex-1 px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none focus:border-amber-500/50" />
            <button onClick={() => { setShowPw(false); setPwInput(''); }}
              className="px-2 py-1.5 text-xs text-neutral-500 hover:text-white">Cancel</button>
          </div>
        )}
      </div>

      {/* Expiry date */}
      <div className="rounded-2xl p-3" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-neutral-400 font-bold mb-0.5">Expiry date</div>
            <div className="text-xs text-neutral-300">
              {expiresAt
                ? <>📅 Auto-revokes on <b>{expiresAt}</b> (end of day, UTC)</>
                : <>♾ Never expires — runs until you revoke manually</>}
            </div>
          </div>
          {expiryStatus(expiresAt) && (
            <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded ${
              ({ red: 'bg-red-500/20 text-red-300', amber: 'bg-amber-500/20 text-amber-300',
                 neutral: 'bg-white/10 text-neutral-300', green: 'bg-green-500/15 text-green-300' })[expiryStatus(expiresAt).tone]
            }`}>
              {expiryStatus(expiresAt).label}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input type="date" value={expiresAt}
            min={new Date().toISOString().slice(0, 10)}
            onChange={e => { setExpiresAt(e.target.value); setDirty(true); }}
            data-testid="share-expiry-input"
            style={{ colorScheme: 'dark' }}
            className="px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none focus:border-purple-500/50" />
          {expiresAt && (
            <button onClick={() => { setExpiresAt(''); setDirty(true); }}
              data-testid="share-expiry-clear"
              className="px-2 py-1.5 text-xs text-neutral-400 hover:text-white">
              Clear (never expire)
            </button>
          )}
          <div className="flex items-center gap-1 ml-auto">
            {[7, 30, 90].map(d => (
              <button key={d}
                onClick={() => {
                  const dt = new Date();
                  dt.setDate(dt.getDate() + d);
                  setExpiresAt(dt.toISOString().slice(0, 10));
                  setDirty(true);
                }}
                className="px-2 py-1 rounded-md text-[10px] font-bold bg-white/5 text-neutral-300 hover:bg-white/10 hover:text-white border border-white/5">
                +{d}d
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* URL + actions */}
      <div className="rounded-2xl p-3" style={{ background: 'rgba(168,85,247,0.08)', border: '1px solid rgba(168,85,247,0.2)' }}>
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-[10px] uppercase tracking-widest text-purple-400 font-bold">Share URL</div>
          {share.access_count > 0 && (
            <div className="text-[10px] text-neutral-500">
              👁 {share.access_count} view{share.access_count === 1 ? '' : 's'}
            </div>
          )}
        </div>
        <p className="text-xs text-white break-all font-mono mb-3" data-testid="share-url">{url}</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
          <button onClick={copy} data-testid="share-copy"
            className="flex items-center justify-center gap-1 px-2.5 py-1.5 rounded-lg bg-white/5 text-xs font-semibold border border-white/10 hover:bg-white/10">
            {copied ? <><Check size={11} className="text-green-400" /> Copied</> : <><Copy size={11} /> Copy</>}
          </button>
          <a href={waUrl()} target="_blank" rel="noopener noreferrer"
            className="flex items-center justify-center gap-1 px-2.5 py-1.5 rounded-lg bg-green-500/15 text-green-400 border border-green-500/30 text-xs font-semibold hover:bg-green-500/25">
            <WhatsappLogo size={11} weight="fill" /> WA
          </a>
          <a href={url} target="_blank" rel="noopener noreferrer"
            className="flex items-center justify-center gap-1 px-2.5 py-1.5 rounded-lg bg-blue-500/15 text-blue-400 border border-blue-500/30 text-xs font-semibold hover:bg-blue-500/25">
            Preview ↗
          </a>
          <button onClick={handleRotate}
            className="flex items-center justify-center gap-1 px-2.5 py-1.5 rounded-lg bg-amber-500/10 text-amber-400 border border-amber-500/20 text-xs font-semibold hover:bg-amber-500/20">
            ↻ Rotate
          </button>
        </div>
      </div>

      {/* Save bar */}
      {dirty && (
        <button onClick={handleSave} disabled={saving}
          data-testid="share-save-btn"
          className="w-full px-4 py-2.5 rounded-xl bg-purple-500 text-white text-sm font-bold disabled:opacity-50">
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      )}
    </div>
  );
}
